import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from '@tanstack/react-router';
import { Check, Eye, EyeOff, X } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import logo from '@/assets/OKYC logo.webp';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';

import { useRegister } from '../hooks/use-register';

interface PasswordRequirement {
  label: string;
  test: (password: string) => boolean;
}

const PASSWORD_REQUIREMENTS: PasswordRequirement[] = [
  {
    label: 'Be at least 8 characters long',
    test: (password) => password.length >= 8,
  },
  {
    label: 'Be a mixture of letters and numbers',
    test: (password) => /[a-zA-Z]/.test(password) && /[0-9]/.test(password),
  },
  {
    label: 'Contain at least 1 upper case letter',
    test: (password) => /[A-Z]/.test(password),
  },
];

const RegisterFormSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters long'),
});

type RegisterFormValues = z.infer<typeof RegisterFormSchema>;

/**
 * Sign-up form.
 *
 * Registration is open — email and password, nothing to be issued first. It
 * creates an account, not access: the new user verifies their email, completes
 * their details, and then waits for an administrator to approve them. The copy
 * below says so before they start, because a form that asks for a firm's
 * registered address without mentioning the review at the end reads as a
 * sign-up that failed.
 */
export function RegisterForm() {
  const { mutate: register, isPending } = useRegister();
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm<RegisterFormValues>({
    resolver: zodResolver(RegisterFormSchema),
    defaultValues: {
      email: '',
      password: '',
    },
    mode: 'onChange',
  });

  const password = form.watch('password');

  function onSubmit(values: RegisterFormValues) {
    register({ email: values.email, password: values.password });
  }

  return (
    <Card className="w-full max-w-md rounded-[5px] border border-[#D4DAE0]">
      <CardHeader className="space-y-4 text-center">
        <img src={logo} alt="OKYC Logo" className="mx-auto h-16 w-auto" />

        <div className="space-y-2">
          <h1 className="text-[20px] font-semibold leading-[120%] text-[#051923]">
            Create Your Account
          </h1>
          <p className="text-[16px] font-normal leading-[22px] text-[#051923]">
            Welcome to Optimal KYC.
          </p>
        </div>

        <p className="text-[16px] font-normal leading-[22px] text-[#051923]">
          Take Anti Money Laundering compliance to the next level with AI PEP &amp; Sanctions
          Screening and automated Adverse Media Investigation.
        </p>
      </CardHeader>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email Address</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="john.smith@company.com"
                      autoComplete="email"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="************"
                        autoComplete="new-password"
                        className="pr-10"
                        {...field}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Password Requirements */}
            <div className="space-y-2">
              <p className="text-[14px] font-medium text-[#051923]">Password must:</p>
              <ul className="space-y-1">
                {PASSWORD_REQUIREMENTS.map((requirement) => {
                  const isMet = requirement.test(password || '');
                  return (
                    <li key={requirement.label} className="flex items-center gap-2">
                      {isMet ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <X className="h-4 w-4 text-gray-400" />
                      )}
                      <span className={`text-[14px] ${isMet ? 'text-green-600' : 'text-gray-500'}`}>
                        {requirement.label}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Sets the expectation before they invest in the setup form: an
                account is created immediately, access is not. */}
            <p className="rounded-[5px] bg-[#F5F7F9] p-3 text-[14px] leading-[20px] text-[#051923]">
              After signing up you will verify your email and complete your company details. An
              administrator then reviews your application, and we will email you as soon as a
              decision has been made.
            </p>
          </CardContent>

          <CardFooter className="flex flex-col space-y-4">
            <Button
              type="submit"
              className="h-[45px] w-full rounded-[5px] bg-[#131313] text-[18px] font-medium leading-[100%] text-white transition-opacity duration-300 ease-out hover:bg-[#131313]/90"
              disabled={isPending}
            >
              {isPending ? 'Creating account...' : 'Create account'}
            </Button>

            <p className="text-center text-[16px] font-normal leading-[22px] text-[#051923]">
              Already have an account?{' '}
              <Link to="/login" className="font-semibold leading-[140%] text-[#131313] underline">
                Login
              </Link>
            </p>
          </CardFooter>
        </form>
      </Form>
    </Card>
  );
}
